import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import supabase from '../config/supabase';
import DatabaseColumn from './cards/DatabaseColumn';
import ItemCardContainer from './cards/ItemCardContainer';
import { getPresetConfig, getStylingTheme } from './cards/ItemCardConfig';
import { directQuery } from '../utils/directFetch';
import GroupVendorOrderEditor from './GroupVendorOrderEditor';
import { useVendorDirectory } from '../utils/useVendorDirectory';
import { useMisuseDetection } from '../utils/useMisuseDetection';
import { pickNextTemplateColor } from '../utils/templateColor';
import {
  PROGRESSIVE_THRESHOLD,
  computeEffectiveVisibleCount,
  nextRevealCount
} from '../utils/progressiveReveal';
import { fetchOptionsForItems } from '../services/purchaseOptionService';
import { groupIdentityFor } from '../utils/relatedItemGroups';
import { fetchWorkingSetGroupsForStore } from '../services/purchasingPlanService';
import { optionSummaryLine } from '../utils/purchaseOptions';

const normalizeLocationKey = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// Group-aware when the store's working-set groups are supplied: a related
// group is ONE countable thing, so an AI operation addressed to any member
// matches every member's row at that location (relatedItemGroups
// groupIdentityFor; the count-through's identity rule).
const rowKey = (row, groups = null) =>
  `${groupIdentityFor(groups, row.inventory_item_id || row.id)}::${normalizeLocationKey(row.location)}`;

const hasParValue = (value) => value !== null && value !== undefined && value !== '';

// A database-browser or AI addition names an existing inventory item. Read
// PAR from that authoritative inventory row instead of manufacturing zero;
// `??` deliberately preserves an explicitly stored numeric zero.
export const parFieldsForGroupAddition = (addition = {}, inventoryItems = []) => {
  const inventoryItemId = addition.inventory_item_id ?? addition.id;
  const inventoryRow = (inventoryItems || []).find((row) => (
    row?.id != null && inventoryItemId != null && String(row.id) === String(inventoryItemId)
  ));
  // The current resolver carries PAR and provenance as one catalog-derived
  // pair. Trust that pair, including an explicit zero. A legacy resolution
  // carried a manufactured par_level: 0 with no provenance; only that older
  // shape falls back to the already-loaded inventory row.
  const source = Object.prototype.hasOwnProperty.call(addition, 'par_defaulted')
    ? addition
    : (inventoryRow || addition);
  return {
    par_level: source?.par_level ?? null,
    par_defaulted: source?.par_defaulted === true,
  };
};

// A template row owns its explicit PAR, including zero. Only a genuinely
// missing row value inherits the current inventory PAR and provenance.
export const resolveLoadedTemplateParFields = (templateRow = {}, inventoryRow = {}) => {
  if (hasParValue(templateRow.par_level)) {
    return {
      par_level: templateRow.par_level,
      par_defaulted: templateRow.par_defaulted === true,
    };
  }
  return {
    par_level: inventoryRow.par_level ?? '',
    par_defaulted: inventoryRow.par_defaulted === true,
  };
};

export const buildTemplateItemInsertRows = (source = [], templateId, storeId) =>
  source.map((item, index) => ({
    template_id: templateId,
    inventory_item_id: item.inventory_item_id || item.id,
    location: item.location || 'No Location',
    template_sort_position: (index + 1) * 10,
    include_in_count: true,
    par_level: item.par_level === '' || item.par_level == null ? null : item.par_level,
    par_unit: item.par_unit || item.unit || 'each',
    par_defaulted: item.par_defaulted === true,
    store_id: storeId,
  }));

/**
 * Group editor (count template UI). Table/API names stay count_templates / count_template_items.
 */
const GroupEditor = ({ onSave, onCancel, editingGroup = null, onToast }) => {
  const [groupName, setGroupName] = useState(editingGroup?.name || '');
  const [groupItems, setGroupItems] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [userStore, setUserStore] = useState(null);
  const [orderCalculationsEnabled, setOrderCalculationsEnabled] = useState(
    editingGroup?.order_calculations_enabled || false
  );
  const [browserFilteredItems, setBrowserFilteredItems] = useState([]);
  const [browserCheckedItemIds, setBrowserCheckedItemIds] = useState([]);
  const [groupCheckedItemIds, setGroupCheckedItemIds] = useState(new Set());
  // True while a template's items are being fetched, so the editor shows a
  // spinner instead of looking frozen. visibleCount drives progressive reveal
  // of the (potentially huge) group list.
  const [itemsLoading, setItemsLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const browserApiRef = useRef(null);
  // Purchase-option counts per inventory item, loaded non-fatally for the
  // visible group rows so the list can note "N active purchase options".
  // Cached by item id; each id is only requested once per editor mount.
  const [purchaseOptionsByItemId, setPurchaseOptionsByItemId] = useState({});
  const purchaseOptionsRequestedRef = useRef(new Set());
  // The store's working-set relationship groups, for group-aware AI-op
  // identity (rowKey). Null on any failure — ops then match item-scoped,
  // exactly as before the count-through.
  const [workingSetGroups, setWorkingSetGroups] = useState(null);

  const { runDetection, modalElement } = useMisuseDetection(userStore?.store_id);

  const { resolveVendor } = useVendorDirectory();

  const notify = useCallback(
    (message, type = 'success') => {
      if (typeof onToast === 'function') {
        onToast(message, type);
      } else {
        alert(message);
      }
    },
    [onToast]
  );

  useEffect(() => {
    let cancelled = false;
    const groupsStoreId = userStore?.store_id;
    if (!groupsStoreId) return undefined;
    fetchWorkingSetGroupsForStore(groupsStoreId)
      .then((groups) => { if (!cancelled) setWorkingSetGroups(groups); })
      .catch(() => { if (!cancelled) setWorkingSetGroups(null); });
    return () => { cancelled = true; };
  }, [userStore?.store_id]);

  useEffect(() => {
    const storedUser = localStorage.getItem('currentUser');
    const storedStore = localStorage.getItem('currentStore');
    if (!storedUser || !storedStore) {
      console.error('No user or store found in localStorage');
      return;
    }
    const authUser = JSON.parse(storedUser);
    const parsedStore = JSON.parse(storedStore);
    const storeId = parsedStore?.id;
    if (!storeId) {
      console.error('No store id found in currentStore');
      return;
    }
    setUser(authUser);
    setUserStore({
      store_id: storeId,
      stores: { id: storeId, name: parsedStore.name || 'Current Store' }
    });
  }, []);

  const loadAvailableItems = useCallback(async () => {
    if (!userStore) return;
    const storeId = userStore.store_id;
    if (!storeId || isNaN(storeId)) {
      console.error('Invalid store id, skipping inventory load');
      setAvailableItems([]);
      return;
    }
    const { data: items, error: itemsError } = await directQuery('inventory_items', {
      select: '*',
      eq: { store_id: storeId },
      order: { column: 'description', ascending: true },
      fetchAll: true,
      pageSize: 200
    });
    if (itemsError) {
      console.error('Error loading inventory items:', itemsError);
      setAvailableItems([]);
      return;
    }
    setAvailableItems(items || []);
  }, [userStore]);

  const loadGroupItemsFromTemplate = useCallback(
    async (templateId) => {
      setItemsLoading(true);
      try {
        let rows = null;
        const { data: joined, error: joinErr } = await supabase
          .from('count_template_items')
          .select('*, inventory_items(*)')
          .eq('template_id', templateId)
          .order('template_sort_position', { ascending: true });

        if (!joinErr && joined) {
          rows = joined;
        } else {
          const { data: bare, error: bareErr } = await supabase
            .from('count_template_items')
            .select('*')
            .eq('template_id', templateId)
            .order('template_sort_position', { ascending: true });
          if (bareErr) throw bareErr;
          const invById = {};
          (availableItems || []).forEach((inv) => {
            invById[inv.id] = inv;
          });
          rows = (bare || []).map((row) => ({
            ...row,
            inventory_items: invById[row.inventory_item_id] || {}
          }));
        }

        const flat = (rows || []).map((row, index) => {
          const inv = row.inventory_items || {};
          const invId = row.inventory_item_id;
          const parFields = resolveLoadedTemplateParFields(row, inv);
          return {
            id: invId,
            inventory_item_id: invId,
            description: inv.description || 'Unknown Item',
            name: inv.description,
            location: row.location || inv.location || 'No Location',
            vendor: inv.vendor,
            brand: inv.brand,
            category: inv.category,
            unit: inv.unit || 'each',
            ...parFields,
            par_unit: row.par_unit || inv.unit || 'each',
            // No min_order_qty seed. `inventory_items.moq` is a legacy,
            // unit-ambiguous column and `?? 1` invented a minimum for every item
            // that had none — a vendor fact conjured from nothing.
            count_position: index + 1
          };
        });
        setGroupItems(flat);
      } catch (e) {
        console.error('Error loading group items:', e);
        notify('Could not load group items.', 'error');
      } finally {
        setItemsLoading(false);
      }
    },
    [notify, availableItems]
  );

  useEffect(() => {
    if (user && userStore) {
      loadAvailableItems();
    }
  }, [user, userStore, loadAvailableItems]);

  useEffect(() => {
    if (editingGroup?.id && userStore) {
      setGroupName(editingGroup.name || '');
      setOrderCalculationsEnabled(editingGroup.order_calculations_enabled || false);
      loadGroupItemsFromTemplate(editingGroup.id);
    } else {
      setGroupItems([]);
      setGroupName('');
      setOrderCalculationsEnabled(false);
    }
  }, [editingGroup, userStore, loadGroupItemsFromTemplate]);

  // Restart the progressive reveal whenever a different template is opened.
  useEffect(() => {
    setVisibleCount(0);
  }, [editingGroup?.id]);

  // Progressively grow the number of revealed group rows toward the full count
  // without blocking the main thread. Keyed on the item count (and template id)
  // so PAR edits — which keep the count constant — never restart the
  // reveal, while add/remove and switching templates do.
  useEffect(() => {
    const total = groupItems.length;
    if (total <= PROGRESSIVE_THRESHOLD) {
      setVisibleCount(total);
      return undefined;
    }
    let cancelled = false;
    let raf = 0;
    const step = () => {
      raf = requestAnimationFrame(() => {
        if (cancelled) return;
        setVisibleCount((current) => {
          const next = nextRevealCount(current, total);
          if (next < total) step();
          return next;
        });
      });
    };
    step();
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [groupItems.length, editingGroup?.id]);

  // Handlers below use functional setState and are wrapped in useCallback so
  // their references stay stable across renders — a prerequisite for the
  // memoized ItemCards to skip re-rendering when an unrelated row changes.
  const addInventoryRowToGroup = useCallback((invRow) => {
    const invId = invRow.id;
    if (!invId) return;
    setGroupItems((prev) => {
      if (prev.some((g) => (g.inventory_item_id || g.id) === invId)) return prev;
      const loc = invRow.location || 'No Location';
      const parFields = parFieldsForGroupAddition(invRow);
      const newItem = {
        id: invId,
        inventory_item_id: invId,
        description: invRow.description,
        name: invRow.description,
        location: loc,
        vendor: invRow.vendor,
        brand: invRow.brand,
        category: invRow.category,
        ...parFields,
        count_position: prev.length + 1,
        unit: invRow.unit || 'each',
        par_unit: invRow.unit || 'each'
      };
      return [...prev, newItem];
    });
  }, []);

  const handleItemMove = useCallback((location, fromIndex, toIndex) => {
    setGroupItems((prev) => {
      const locationItems = prev.filter((item) => item.location === location);
      const otherItems = prev.filter((item) => item.location !== location);
      const reordered = [...locationItems];
      const [moved] = reordered.splice(fromIndex, 1);
      if (!moved) return prev;
      reordered.splice(toIndex, 0, moved);
      const updated = reordered.map((item, index) => ({
        ...item,
        count_position: index + 1
      }));
      return [...otherItems, ...updated];
    });
  }, []);

  const handleItemRemove = useCallback((itemId) => {
    setGroupCheckedItemIds((prev) => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
    setGroupItems((prev) => {
      const next = prev.filter((item) => item.id !== itemId);
      return next.map((item, index) => ({ ...item, count_position: index + 1 }));
    });
  }, []);

  const handleUpdatePar = useCallback((itemId, _location, value, unit) => {
    setGroupItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, par_level: parseFloat(value) || 0, par_unit: unit, par_defaulted: false }
          : item
      )
    );
  }, []);

  const persistTemplateItems = async (templateId, rows = null) => {
    const source = Array.isArray(rows) ? rows : groupItems;
    const templateItemsToSave = buildTemplateItemInsertRows(source, templateId, userStore.store_id);
    const { error: itemsError } = await supabase.from('count_template_items').insert(templateItemsToSave);
    if (itemsError) throw itemsError;
  };

  const applyAiOperationsToRows = (prevRows, approvedOps) => {
    let next = prevRows.map((item) => ({ ...item }));
    const findIdx = (invId, location) =>
      next.findIndex((row) => (row.inventory_item_id || row.id) === invId && normalizeLocationKey(row.location) === normalizeLocationKey(location));

    (approvedOps || []).forEach((op) => {
      const r = op.resolution || {};
      if (op.type === 'add_items') {
        (r.additions || []).forEach((addition) => {
          if (findIdx(addition.inventory_item_id, addition.target_location) >= 0) return;
          const parFields = parFieldsForGroupAddition(addition, availableItems);
          next.push({
            id: addition.inventory_item_id,
            inventory_item_id: addition.inventory_item_id,
            description: addition.item_name,
            name: addition.item_name,
            category: addition.category,
            vendor: addition.vendor,
            brand: addition.brand,
            location: addition.target_location,
            ...parFields,
            par_unit: addition.unit || 'each',
            unit: addition.unit || 'each',
            count_position: next.length + 1,
          });
        });
      } else if (op.type === 'remove_items') {
        const keys = new Set((r.removals || []).map((row) => rowKey(row, workingSetGroups)));
        next = next.filter((row) => !keys.has(rowKey(row, workingSetGroups)));
      } else if (op.type === 'move_items') {
        (r.transfers || []).forEach((t) => {
          const sourceIdx = findIdx(t.inventory_item_id, t.from_location);
          if (sourceIdx < 0) return;
          const existsAtTargetIdx = findIdx(t.inventory_item_id, t.to_location);
          if (existsAtTargetIdx >= 0) {
            next.splice(sourceIdx, 1);
          } else {
            next[sourceIdx] = { ...next[sourceIdx], location: t.to_location };
          }
        });
      } else if (op.type === 'copy_items') {
        (r.transfers || []).forEach((t) => {
          if (findIdx(t.inventory_item_id, t.to_location) >= 0) return;
          const sourceIdx = findIdx(t.inventory_item_id, t.from_location);
          if (sourceIdx < 0) return;
          next.push({ ...next[sourceIdx], location: t.to_location });
        });
      } else if (op.type === 'rename_location') {
        const fromKey = normalizeLocationKey(r.from_location || '');
        const toLoc = r.to_location || '';
        const sourceRows = next.filter((row) => normalizeLocationKey(row.location) === fromKey);
        const targetKey = normalizeLocationKey(toLoc);
        sourceRows.forEach((row) => {
          const invId = row.inventory_item_id || row.id;
          const existsAtTarget = next.some((other) => other !== row && (other.inventory_item_id || other.id) === invId && normalizeLocationKey(other.location) === targetKey);
          if (existsAtTarget) {
            next = next.filter((candidate) => candidate !== row);
          } else {
            row.location = toLoc;
          }
        });
      } else if (op.type === 'set_par') {
        const keys = new Set((r.updates || []).map((u) => rowKey(u, workingSetGroups)));
        next = next.map((row) => keys.has(rowKey(row, workingSetGroups))
          ? { ...row, par_level: r.par_value, par_defaulted: false }
          : row);
      } else if (op.type === 'set_moq') {
        // Retained so an approved AI op is never an unhandled type, but it has
        // no effect this editor can show or save: the card no longer renders a
        // minimum (showMOQ:false) and persistTemplateItems writes par_level /
        // par_unit only. Catalogued as dead in docs/moq-ownership-audit.md.
        const keys = new Set((r.updates || []).map((u) => rowKey(u, workingSetGroups)));
        next = next.map((row) => keys.has(rowKey(row, workingSetGroups)) ? { ...row, min_order_qty: r.moq_value } : row);
      } else if (op.type === 'sort_location') {
        const locKey = normalizeLocationKey(r.location);
        const order = new Map();
        (r.sorted || []).forEach((s, idx) => {
          order.set(rowKey(s, workingSetGroups), idx);
        });
        const inLoc = [];
        const outLoc = [];
        next.forEach((row) => {
          if (normalizeLocationKey(row.location) === locKey) inLoc.push(row);
          else outLoc.push(row);
        });
        inLoc.sort((a, b) => (order.get(rowKey(a, workingSetGroups)) ?? 1e9) - (order.get(rowKey(b, workingSetGroups)) ?? 1e9));
        next = [...outLoc, ...inLoc];
      }
    });
    return next.map((row, index) => ({ ...row, count_position: index + 1 }));
  };

  const handleApplyAiPlan = async (approvedOps) => {
    if (!Array.isArray(approvedOps) || !approvedOps.length) return;
    const nextRows = applyAiOperationsToRows(groupItems, approvedOps);
    setGroupItems(nextRows);

    if (editingGroup?.id && userStore?.store_id) {
      try {
        const { error: delErr } = await supabase
          .from('count_template_items')
          .delete()
          .eq('template_id', editingGroup.id);
        if (delErr) throw delErr;
        await persistTemplateItems(editingGroup.id, nextRows);
        notify('AI changes saved.', 'success');
        // Notify the Counts overview to rescan category icons for this template.
        document.dispatchEvent(new CustomEvent('refreshCountInterface'));
        if (onSave) {
          onSave({
            id: editingGroup.id,
            name: groupName,
            items: nextRows,
            totalItems: nextRows.length,
            order_calculations_enabled: orderCalculationsEnabled,
          });
        }
      } catch (error) {
        console.error('Error persisting AI changes:', error);
        notify(`Could not save AI changes: ${error.message}`, 'error');
      }
    } else {
      notify('AI changes applied. Click Save group to persist.', 'success');
    }
  };

  const handleSaveGroup = async () => {
    if (!groupName.trim()) {
      notify('Please enter a group name.', 'error');
      return;
    }
    if (groupItems.length === 0) {
      notify('Add at least one item to the group.', 'error');
      return;
    }
    if (!user || !userStore) {
      notify('Session not loaded. Please refresh and try again.', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingGroup?.id) {
        const { error: updErr } = await supabase
          .from('count_templates')
          .update({
            name: groupName.trim(),
            description: `Group with ${groupItems.length} items`,
            order_calculations_enabled: orderCalculationsEnabled
          })
          .eq('id', editingGroup.id);
        if (updErr) throw updErr;

        const { error: delErr } = await supabase
          .from('count_template_items')
          .delete()
          .eq('template_id', editingGroup.id);
        if (delErr) throw delErr;

        await persistTemplateItems(editingGroup.id);
        notify(`Group "${groupName}" saved.`, 'success');
        const savedGroupData = {
          id: editingGroup.id,
          name: groupName,
          items: groupItems,
          totalItems: groupItems.length,
          order_calculations_enabled: orderCalculationsEnabled
        };
        // Notify the Counts overview to rescan category icons for this template.
        document.dispatchEvent(new CustomEvent('refreshCountInterface'));
        // GroupEditor is the persistent EOP template rename path: count_templates
        // named "End of Period Mission" / "End of Period Marina" etc. are created
        // and renamed here. Detection passes editingGroup.id so the modal only
        // fires when THIS renamed template participates in a suspicious pair.
        await runDetection(() => { if (onSave) onSave(savedGroupData); }, undefined, editingGroup.id);
      } else {
        // Pick a least-used palette color from this store's existing templates
        // so a new template is visually distinguishable from its neighbors.
        // The Postgres trigger does the same thing if template_color is left
        // null, but assigning here means the response below already carries
        // the final color back to the UI for immediate use.
        let templateColor = null;
        try {
          const { data: existing } = await supabase
            .from('count_templates')
            .select('template_color')
            .eq('store_id', userStore.store_id);
          templateColor = pickNextTemplateColor(existing || []);
        } catch (_) {
          templateColor = pickNextTemplateColor([]);
        }

        const { data: template, error: templateError } = await supabase
          .from('count_templates')
          .insert({
            name: groupName.trim(),
            description: `Group with ${groupItems.length} items`,
            store_id: userStore.store_id,
            created_by: user.id,
            order_calculations_enabled: orderCalculationsEnabled,
            template_color: templateColor,
            created_at: new Date().toISOString()
          })
          .select()
          .single();
        if (templateError) throw templateError;

        await persistTemplateItems(template.id);
        notify(`Group "${groupName}" created.`, 'success');
        const newGroupData = {
          id: template.id,
          name: groupName,
          items: groupItems,
          totalItems: groupItems.length,
          order_calculations_enabled: orderCalculationsEnabled
        };
        // Notify the Counts overview to rescan category icons for the new template.
        document.dispatchEvent(new CustomEvent('refreshCountInterface'));
        // GroupEditor is also the EOP template create path — detection passes
        // template.id so warnings only fire when this new template is the one
        // involved in a suspicious location-differentiating pair.
        await runDetection(() => { if (onSave) onSave(newGroupData); }, undefined, template.id);
      }
    } catch (error) {
      console.error('Error saving group:', error);
      notify('Error saving group: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleGroupItemSelection = useCallback((item, checked) => {
    setGroupCheckedItemIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(item.id);
      else next.delete(item.id);
      return next;
    });
  }, []);

  // Stable no-op for card/row click handlers that intentionally do nothing.
  const noop = useCallback(() => {}, []);

  const handleSelectAllGroupItems = () => {
    setGroupCheckedItemIds(new Set(groupItems.map((item) => item.id)));
  };

  const handleDeselectAllGroupItems = () => {
    setGroupCheckedItemIds(new Set());
  };

  const handleRemoveSelectedGroupItems = () => {
    if (!groupCheckedItemIds.size) {
      notify('Select one or more group items to remove.', 'error');
      return;
    }
    setGroupItems((prev) => {
      const next = prev.filter((item) => !groupCheckedItemIds.has(item.id));
      return next.map((item, index) => ({ ...item, count_position: index + 1 }));
    });
    notify(
      `${groupCheckedItemIds.size} item${groupCheckedItemIds.size === 1 ? '' : 's'} removed`,
      'success'
    );
    setGroupCheckedItemIds(new Set());
  };

  const handleAddSelectedItems = () => {
    if (!browserCheckedItemIds.length) {
      notify('Select one or more items from the database list first.', 'error');
      return;
    }
    const sourceById = new Map(availableItems.map((item) => [item.id, item]));
    const idsInGroup = new Set(
      groupItems.map((item) => item.inventory_item_id || item.id).filter(Boolean)
    );
    const rowsToAdd = browserCheckedItemIds
      .map((id) => sourceById.get(id))
      .filter((row) => row && !idsInGroup.has(row.id));

    if (!rowsToAdd.length) {
      notify('No new selected items to add.', 'success');
      return;
    }

    setGroupItems((prev) => {
      let next = [...prev];
      rowsToAdd.forEach((row) => {
        const invId = row.id;
        if (next.some((g) => (g.inventory_item_id || g.id) === invId)) return;
        const parFields = parFieldsForGroupAddition(row);
        next.push({
          id: invId,
          inventory_item_id: invId,
          description: row.description,
          name: row.description,
          location: row.location || 'No Location',
          vendor: row.vendor,
          brand: row.brand,
          category: row.category,
          ...parFields,
          count_position: next.length + 1,
          unit: row.unit || 'each',
          par_unit: row.unit || 'each'
        });
      });
      return next.map((item, index) => ({ ...item, count_position: index + 1 }));
    });
    notify(`${rowsToAdd.length} item${rowsToAdd.length === 1 ? '' : 's'} added`, 'success');
    setBrowserCheckedItemIds([]);
    if (browserApiRef.current?.uncheckAll) {
      browserApiRef.current.uncheckAll();
    }
  };

  const handleSelectAllAvailableItems = useCallback(() => {
    if (browserApiRef.current?.checkAll) {
      browserApiRef.current.checkAll();
    }
  }, []);

  const handleDeselectAllAvailableItems = useCallback(() => {
    if (browserApiRef.current?.uncheckAll) {
      browserApiRef.current.uncheckAll();
    }
  }, []);

  const groupVendorNames = useMemo(
    () =>
      Array.from(
        new Set(groupItems.map((item) => resolveVendor((item.vendor || '').trim())).filter(Boolean))
      ).sort((left, right) => left.localeCompare(right)),
    [groupItems, resolveVendor]
  );

  // Group-list rendering: reveal progressively for very large templates so the
  // first rows paint immediately. effectiveVisibleCount floors at INITIAL_CHUNK
  // so the first paint is never empty even before the reveal effect has run.
  const totalGroupItems = groupItems.length;
  const effectiveVisibleCount = computeEffectiveVisibleCount(totalGroupItems, visibleCount);
  const isStreaming = effectiveVisibleCount < totalGroupItems;
  const visibleGroupItems = useMemo(
    () =>
      effectiveVisibleCount >= groupItems.length
        ? groupItems
        : groupItems.slice(0, effectiveVisibleCount),
    [groupItems, effectiveVisibleCount]
  );

  // Batch-load purchase-option counts for the currently visible group rows.
  // Display-only: failures are swallowed and the summary lines simply stay
  // hidden (e.g. when the purchase_options table isn't deployed yet). Template
  // membership (count_template_items) is never touched by this.
  useEffect(() => {
    const storeId = userStore?.store_id;
    if (!storeId || visibleGroupItems.length === 0) return undefined;
    const missing = [];
    visibleGroupItems.forEach((item) => {
      const invId = item.inventory_item_id || item.id;
      if (invId === null || invId === undefined) return;
      const key = String(invId);
      if (!purchaseOptionsRequestedRef.current.has(key)) {
        purchaseOptionsRequestedRef.current.add(key);
        missing.push(invId);
      }
    });
    if (missing.length === 0) return undefined;
    let cancelled = false;
    fetchOptionsForItems(storeId, missing)
      .then((grouped) => {
        if (cancelled) return;
        setPurchaseOptionsByItemId((prev) => ({ ...prev, ...(grouped || {}) }));
      })
      .catch((err) => {
        console.warn('[purchase-options] group item summary load failed (non-fatal):', err?.message);
      });
    return () => { cancelled = true; };
  }, [userStore, visibleGroupItems]);

  // getPresetConfig / getStylingTheme return module-level constants, so reading
  // them per render yields stable references that won't defeat ItemCard's memo.
  const groupContainerConfig = getPresetConfig('GROUP_EDITOR').container;
  // showMOQ:false removes the card's minimum-order input from this editor. A
  // minimum belongs to a vendor product, not to the item being put in a count
  // group, and this editor never persisted the value anyway (see
  // persistTemplateItems, which writes par_level / par_unit only).
  const groupCardConfig = { ...getPresetConfig('GROUP_EDITOR').card, showMOQ: false };
  const groupStyling = getStylingTheme('GROUP_EDITOR');

  // The database browser only needs to know which items are already in the
  // group (to exclude them). Membership changes on add/remove, not on PAR
  // edits, so key a referentially stable array off the membership signature —
  // this keeps the browser from re-filtering the whole inventory on every
  // keystroke.
  const groupMembershipKey = groupItems
    .map((item) => item.inventory_item_id || item.id)
    .join('|');
  const browserCurrentItems = useMemo(
    () => groupItems,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupMembershipKey]
  );

  // Memoize the entire right-hand database column. It's an expensive subtree
  // (it can list the store's whole inventory) and nothing inside it depends on
  // the group rows' PAR values — only on membership and its own selection
  // counts — so a PAR edit must not force it to re-render.
  const databaseColumn = useMemo(
    () => (
      <DatabaseColumn
        inventoryData={availableItems}
        currentItems={browserCurrentItems}
        onAddItem={addInventoryRowToGroup}
        onItemClick={noop}
        onFilteredListChange={setBrowserFilteredItems}
        config={{
          title: 'Add items from database',
          subtitle: 'Filter, then add one by one or select multiple',
          icon: 'database',
          showHeader: true,
          headerExtra: (
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none"
                onClick={handleSelectAllAvailableItems}
                disabled={!browserFilteredItems.length}
              >
                Select all
              </button>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none"
                onClick={handleDeselectAllAvailableItems}
                disabled={!browserCheckedItemIds.length}
              >
                Deselect all
              </button>
              <span className="small text-muted">{browserCheckedItemIds.length}</span>
            </div>
          )
        }}
        browserConfig={{
          showSearch: true,
          showFilters: true,
          showAddButton: true,
          showItemCount: true,
          expandableItems: false,
          addButtonText: 'Add',
          compactLayout: true,
          stickyFilters: true,
          selectable: true,
          browserApiRef,
          onCheckedItemsChange: setBrowserCheckedItemIds
        }}
        styling={{
          column: {
            height: '100%',
            border: 'none',
            borderRadius: 0,
            backgroundColor: 'white'
          },
          header: {
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #dee2e6',
            padding: '0.5rem 0.75rem'
          },
          browserContainer: { minHeight: 0, display: 'flex' },
          browser: {
            container: { minHeight: 0 },
            itemsContainer: { minHeight: 0, overflowY: 'auto' }
          }
        }}
      />
    ),
    [
      availableItems,
      browserCurrentItems,
      browserFilteredItems.length,
      browserCheckedItemIds.length,
      addInventoryRowToGroup,
      handleSelectAllAvailableItems,
      handleDeselectAllAvailableItems,
      noop
    ]
  );

  return (
    <>
    <DndProvider backend={HTML5Backend}>
      <div className="group-editor count-template-creator">
        <div className="card">
          <div className="card-header py-2">
            <h5 className="mb-0 small fw-semibold">
              {editingGroup ? 'Edit group' : 'Create group'}
            </h5>
          </div>
          <div className="card-body pt-2 pb-3">
            <div className="mb-2">
              <div className="row g-2 align-items-end">
                <div className="col-md-8">
                  <label className="form-label small mb-1">Group name</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="e.g. Weekly walk-in, Produce order"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label small mb-1">Order mode</label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="group-order-mode"
                      checked={orderCalculationsEnabled}
                      onChange={(e) => setOrderCalculationsEnabled(e.target.checked)}
                    />
                    <label className="form-check-label small" htmlFor="group-order-mode">
                      {orderCalculationsEnabled ? 'On' : 'Off'}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="row g-0 border rounded"
              style={{ height: 'min(72vh, 700px)', overflow: 'hidden' }}
            >
              <div className="col-md-6 border-end" style={{ minHeight: 0, display: 'flex' }}>
                <div className="h-100 d-flex flex-column" style={{ minHeight: 0 }}>
                  <div className="px-2 py-2 border-bottom bg-light flex-shrink-0">
                    <div className="d-flex justify-content-between align-items-center gap-2">
                      <span className="small fw-semibold text-secondary">
                        Group items
                      </span>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-decoration-none"
                          onClick={handleSelectAllGroupItems}
                          disabled={!groupItems.length}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-decoration-none"
                          onClick={handleDeselectAllGroupItems}
                          disabled={!groupCheckedItemIds.size}
                        >
                          Deselect all
                        </button>
                        <span className="small text-muted">
                          {groupCheckedItemIds.size} selected
                        </span>
                      </div>
                    </div>
                    <p className="small text-muted mb-0 mt-1" style={{ fontSize: '0.72rem' }}>
                      Drag to reorder. Set PAR inline. Select items for bulk remove.
                    </p>
                  </div>
                  <div className="flex-grow-1 px-2 py-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                    {itemsLoading ? (
                      <div className="d-flex align-items-center gap-2 text-muted small py-3 px-1">
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        Loading template items…
                      </div>
                    ) : groupItems.length === 0 ? (
                      <p className="small text-muted mb-0 py-2 px-1">
                        This is where your items will appear when added to the template. Add items in bulk, or open to add one by one.
                      </p>
                    ) : (
                      <>
                        <ItemCardContainer
                          items={visibleGroupItems}
                          onMove={handleItemMove}
                          onRemove={handleItemRemove}
                          onUpdatePar={handleUpdatePar}
                          onItemClick={noop}
                          selectable
                          selectedItemIds={groupCheckedItemIds}
                          onToggleItemSelect={toggleGroupItemSelection}
                          config={groupContainerConfig}
                          cardConfig={groupCardConfig}
                          styling={groupStyling}
                        />
                        {isStreaming && (
                          <div
                            className="d-flex align-items-center gap-2 text-muted py-2 px-1"
                            style={{ fontSize: '0.72rem' }}
                          >
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                              style={{ width: '0.85rem', height: '0.85rem' }}
                            />
                            Loading items… {effectiveVisibleCount} of {totalGroupItems}
                          </div>
                        )}
                        {/* Purchase-option availability footnotes. The shared
                            ItemCard rows have no extension slot, so the small
                            gray "N active purchase options" notes render as a
                            compact per-item list at the bottom of the panel. */}
                        {(() => {
                          const summaries = visibleGroupItems
                            .map((item) => {
                              const invId = item.inventory_item_id || item.id;
                              const options = purchaseOptionsByItemId[invId];
                              if (!(options || []).some((o) => o?.status === 'active')) return null;
                              return {
                                key: `${invId}::${item.location || ''}`,
                                description: item.description || item.name || 'Unknown Item',
                                options,
                              };
                            })
                            .filter(Boolean);
                          if (summaries.length === 0) return null;
                          return (
                            <div className="px-1 pt-1 pb-1" style={{ borderTop: '1px dashed #E8DDD0', marginTop: '4px' }}>
                              {summaries.map((s) => (
                                <div key={s.key} className="text-muted" style={{ fontSize: '0.72rem', lineHeight: 1.5 }}>
                                  {s.description} — {optionSummaryLine(s.options)}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-6" style={{ minHeight: 0, display: 'flex' }}>
                {databaseColumn}
              </div>
            </div>

            <div className="row g-2 mt-2">
              <div className="col-md-6 d-grid">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleRemoveSelectedGroupItems}
                  disabled={!groupCheckedItemIds.size}
                >
                  Remove items from group <span aria-hidden="true">→</span>
                </button>
              </div>
              <div className="col-md-6 d-grid">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleAddSelectedItems}
                  disabled={!browserCheckedItemIds.length}
                >
                  <span aria-hidden="true">←</span> Add selected items
                </button>
              </div>
            </div>

            <GroupVendorOrderEditor
              enabled={orderCalculationsEnabled}
              userStore={userStore}
              vendorNames={groupVendorNames}
              notify={notify}
            />

            <div className="d-flex justify-content-between align-items-center mt-3">
              <button className="btn btn-sm btn-secondary" onClick={onCancel} disabled={saving}>
                Cancel
              </button>
              <button
                className="btn btn-sm btn-success"
                onClick={handleSaveGroup}
                disabled={saving || itemsLoading || !groupName.trim() || groupItems.length === 0}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                    Saving…
                  </>
                ) : (
                  <>
                    <i className="feather icon-save me-1" />
                    Save group
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </DndProvider>

    {modalElement}
    </>
  );
};

export default GroupEditor;
